import type { LucideIcon } from 'lucide-react';
import { Info } from '@/components/Info';

/**
 * 패널 — 제목 + (툴팁에 숨긴) 설명 + 본문.
 *
 * ★★ **설명을 툴팁으로 옮겼다**(2026-10-03, 사용자 지시 — 「텍스트가 많아서 보기
 * 힘들다」). ⚠️ 지운 것이 아니다 — 이 프로젝트의 설명문은 대부분 「왜 이렇게
 * 세는가」라서 **없으면 숫자를 오해한다.**
 *
 * ★ 제목을 키우고 **축색 띠**를 왼쪽에 둔다 — 「어느 화면의 무엇인가」가
 *   색으로 먼저 읽힌다.
 */
export function Panel({
  title,
  note,
  tone,
  testId,
  accent,
  icon: Icon,
  delay = 0,
  children,
}: {
  title?: string | undefined;
  note?: string | undefined;
  tone?: 'error' | undefined;
  testId?: string | undefined;
  /** 축색. 비면 띠를 그리지 않는다. */
  accent?: string | undefined;
  /** 제목 앞 아이콘(lucide). ⚠️ 이모지는 쓰지 않는다 — SUIT 에 없다. */
  icon?: LucideIcon | undefined;
  /** 등장 순서. ★ 격자에서 패널이 **차례로** 떠오른다. */
  delay?: number | undefined;
  children: React.ReactNode;
}) {
  return (
    <section
      // ★ `self-start` — 격자에서 늘어나지 않는다. ⚠️ 늘어나면 3줄짜리 패널이
      //   옆 차트 높이에 맞춰 아래가 통째로 빈다(실측).
      className="animate-rise relative w-full self-start overflow-hidden rounded-lg border border-border bg-card p-panel"
      style={{
        animationDelay: `${delay * 60}ms`,
        ...(tone === 'error' ? { borderColor: 'var(--lens-error)' } : null),
        // ★ 어두운 바탕에서는 그림자가 안 보인다 — **축색 번짐**으로 띄운다.
        ...(accent !== undefined
          ? { boxShadow: `0 0 0 1px color-mix(in srgb, ${accent} 22%, transparent)` }
          : null),
      }}
      data-panel={testId ?? title}
    >
      {accent !== undefined ? (
        <span
          className="animate-sweep absolute inset-x-0 top-0 h-px"
          style={{
            background: `linear-gradient(90deg, ${accent}, transparent)`,
            animationDelay: `${delay * 60 + 120}ms`,
          }}
          aria-hidden="true"
        />
      ) : null}
      {title !== undefined ? (
        <header className="mb-lg flex items-center gap-sm">
          {Icon !== undefined ? (
            <Icon
              size={18}
              strokeWidth={1.9}
              aria-hidden="true"
              style={{ color: accent ?? 'var(--lens-text-muted)' }}
              className="shrink-0"
            />
          ) : null}
          <h2 className="text-title-lg">{title}</h2>
          {note !== undefined ? <Info text={note} label={`${title} 설명`} /> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

/**
 * 축 KPI 큰 숫자.
 *
 * ★★ **숫자를 크게.** 분석 도구에서 가장 먼저 읽혀야 하는 것이다.
 * ⚠️ `status` 가 `unrated` 면 칩을 그리지 않는다 — 넷 중 셋은 **판정하지 않는다**
 * (OBXYD 회신 25). 늘 빨간 불은 정보가 0 이다.
 */
export function AxisHeadline({
  label,
  value,
  targetText,
  status,
  accent,
  note,
  icon: Icon,
}: {
  label: string;
  value: string;
  targetText: string;
  status: string;
  accent?: string | undefined;
  note?: string | undefined;
  icon?: LucideIcon | undefined;
}) {
  const tone =
    status === 'good'
      ? { bg: 'var(--lens-accent-success)', fg: 'var(--lens-success)', text: '정상' }
      : status === 'warn'
        ? { bg: 'var(--lens-accent-warning)', fg: 'var(--lens-warning)', text: '경고' }
        : status === 'bad'
          ? { bg: 'var(--lens-accent-error)', fg: 'var(--lens-error)', text: '위반' }
          : null;
  return (
    // ★ `self-start` — 격자에서 **옆 패널 높이에 맞춰 늘어나지 않는다**.
    //   ⚠️ 늘어나면 큰 숫자 아래가 통째로 비어 「고장난 칸」처럼 보인다(실측).
    <div
      className="animate-rise relative w-full self-start overflow-hidden rounded-lg border border-border bg-card p-xl"
      style={
        accent !== undefined
          ? {
              // ★ 축색이 카드 위쪽에서 **번진다** — 어두운 바탕에서 가장 먼저 읽힌다.
              background: `radial-gradient(120% 90% at 0% 0%, color-mix(in srgb, ${accent} 13%, transparent), transparent 60%), var(--lens-surface)`,
              boxShadow: `0 0 0 1px color-mix(in srgb, ${accent} 26%, transparent)`,
            }
          : undefined
      }
    >
      {accent !== undefined ? (
        <span
          className="animate-sweep absolute inset-x-0 top-0 h-0.5"
          style={{ background: `linear-gradient(90deg, ${accent}, transparent 85%)` }}
          aria-hidden="true"
        />
      ) : null}
      <div className="flex items-center gap-sm">
        {Icon !== undefined ? (
          <Icon
            size={18}
            strokeWidth={1.9}
            aria-hidden="true"
            style={{ color: accent }}
            className="shrink-0"
          />
        ) : null}
        <span className="text-title-md text-muted-foreground">{label}</span>
        {tone !== null ? (
          <span
            className="rounded-pill px-md py-xs text-label-md"
            style={{ background: tone.bg, color: tone.fg }}
          >
            {tone.text}
          </span>
        ) : null}
        {note !== undefined ? <Info text={note} label={`${label} 설명`} /> : null}
      </div>
      <div
        className="mt-sm text-display-lg tabular-nums"
        style={accent !== undefined ? { color: accent } : undefined}
      >
        {value}
      </div>
      <div className="mt-xs text-label-md text-muted-foreground">{targetText}</div>
    </div>
  );
}

/**
 * 경고 배너 — ★ **빈 문자열이면 아예 그리지 않는다.**
 *
 * ⚠️ 이것이 집계 쪽 규약이다(`notices.ts`). 「없음」을 0% 로 적으면
 * 「문제 없음」으로 읽힌다.
 */
export function Notice({ text }: { text: string }) {
  if (text === '') return null;
  return (
    <div
      className="flex gap-md rounded-md border-l-4 px-lg py-md text-body-md"
      style={{
        // ⚠️⚠️ **불투명하게 칠한다.** 전에는 반투명 노랑(`--lens-accent-warning`)
        // 이라 **어두운 조상이 있어야만** 글자가 읽혔다 — 밝은 면 위에 놓으면
        // 흰 글자가 연노랑에 묻혀 **경고가 통째로 안 보인다**(2026-10-04 실측,
        // 디자인 시스템 카드에서 드러났다).
        // ★ 경고는 어디에 놓이든 읽혀야 한다 — 그래서 바탕을 스스로 만든다.
        background: 'color-mix(in srgb, var(--lens-warning) 15%, var(--lens-surface))',
        borderColor: 'var(--lens-warning)',
        color: 'var(--lens-text)',
      }}
      role="status"
    >
      {text}
    </div>
  );
}
